import React, { FormEvent, useEffect, useState } from 'react'
import { Form, useNavigate } from 'react-router-dom';
import { Alert, Button, Checkbox, FormControlLabel, IconButton, InputLabel, styled, TextField, Tooltip, tooltipClasses, TooltipProps } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { useConfirm } from 'material-ui-confirm';

import { IMaterial } from '../../types/material';
import { IFuzzySearchResult } from '../../types/system';

import { AppConfig } from '../../config/appConfig';

import { ErrorProvider } from '../../classes/errorProvider';
import { MaterialService } from '../../services/materialService';
import { UtilityService } from '../../services/utilityService';

import { Icons } from '../icons';
import WorkingModal, { IWorkingModalProps } from '../workingModal';

import '../../styles/materialDetailsForm.scss';

export interface IMaterialDetailsForm {
	action: "add" | "edit";
	id?: string;
	details: IMaterial;
	reloadData: () => void;
}

const ExternalNameToolTip = styled(({ className, ...props }: TooltipProps) => (
	<Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
	[`& .${tooltipClasses.arrow}`]: {
		color: theme.palette.common.black,

	},
	[`& .${tooltipClasses.tooltip}`]: {
		maxWidth: 350,
		fontSize: theme.typography.pxToRem(14),
		backgroundColor: theme.palette.common.black,
		color: theme.palette.common.white,
	},
}));

const MaterialDetailsForm: React.FunctionComponent<IMaterialDetailsForm> = ({ action, id, details, reloadData }) => {
	const confirm = useConfirm();
	const navigate = useNavigate();
	const [formFields, setFormFields] = useState<IMaterial>(details);
	const [originalMaterialName, setOriginalMaterialName] = useState<string>(details?.material);
	const [isSaving, setIsSaving] = useState(false);
	const [isCheckingNameForDupes, setIsCheckingNameForDupes] = useState(false);
	const [errorMessage, setErrorMessage] = useState<string | undefined>();
	const [successMessage, setSuccessMessage] = useState<string | undefined>();
	const [workingModalParams, setWorkingModalParams] = useState<IWorkingModalProps | undefined>(undefined);

	useEffect(() => {
		if (formFields?.category?.toLowerCase()?.trim() == "hazardous") {
			setFormFields((prev) => ({ ...prev, isHazardous: true }));
		}
	}, [formFields.category]);

	const doSave = async () => {
		clearSaveResult();
		setIsSaving(true);
		setWorkingModalParams({ params: { content: "Saving material details..." } });
		const payload: IMaterial = { ...formFields };
		payload.category = (formFields?.category?.toLowerCase()?.trim() == "hazardous") ? "Hazardous" : formFields?.category;

		if (action == "add") {
			MaterialService.addMaterial(payload)
				.then((response) => {
					console.log('MaterialService.addMaterial: SUCCESS', response);
					setSuccessMessage("Material has been added.");
					var material = MaterialService.mapMaterial(response.data);
					setTimeout(() => {
						navigate("/material/" + material.id);
					}, AppConfig.addSuccessToastMs);
				})
				.catch((error) => {
					console.log('MaterialService.addMaterial: ERROR', error);
					setErrorMessage(ErrorProvider.getError(error));
					setIsSaving(false);
				})
				.finally(() => {					
					setWorkingModalParams(undefined);
					window.scroll(0, 0);
				});
		}
		else if (action == "edit" && id) {
			MaterialService.updateMaterial(payload)
				.then((response) => {
					console.log('MaterialService.updateMaterial: SUCCESS', response);
					reloadData();
					setSuccessMessage("Material details have been saved.");
					setTimeout(() => {
						setSuccessMessage(undefined);
					}, AppConfig.editSuccessToastMs);
				})
				.catch((error) => {
					console.log('MaterialService.updateMaterial: ERROR', error);
					setErrorMessage(ErrorProvider.getError(error));
				})
				.finally(() => {
					setIsSaving(false);
					setWorkingModalParams(undefined);
					window.scrollTo(0, 0);
				});
		}
	}

	const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
		// Fuzzy check existing customer names for potential duplicates before saving		
		event.preventDefault();
		setErrorMessage(undefined);

		// If the name wasn't changed then don't bother checking for duplicates
		if (action == "edit" && formFields?.material == originalMaterialName) {
			await doSave();
			return;
		}

		setIsCheckingNameForDupes(true);

		MaterialService.fuzzySearchForDuplicateMaterials({ name: formFields?.material, excludeId: action == "edit" ? +id : undefined, includeInactive: true })
			.then(async (response) => {
				console.log('MaterialService.fuzzySearchForDuplicateMaterialNames: SUCCESS', response);
				const matches: IFuzzySearchResult[] = response.data;
				if (matches.length > 0) {
					if (await UtilityService.ConfirmationModal(
						confirm,
						<div className="dupes-dialog-title">
							<Icons.Warning fill="#272f50" height="14px"></Icons.Warning>
							Potential Duplicates
						</div>,
						<div className="dupes-dialog-content">
							The name <strong>{formFields.material}</strong> is similar to <strong>{matches.length}</strong> existing material{matches.length > 1 ? "s" : ""}. Please review the conflicts below before proceeding.<br />
							<ul>
								{matches.map((item, index) => (
									<li key={index}>
										<a href={`/material/${item.id}`} target="_blank" rel="noopener noreferrer">
											<Icons.ViewItem height="14px"></Icons.ViewItem>
											{item.name}
										</a>
										{!item.isActive && <span className="inactive"> (Inactive)</span>}
									</li>
								))}
							</ul>
						</div>,
						{
							confirmationText: "Continue",
							confirmationButtonProps: { color: 'primary', variant: 'contained' },
							cancellationText: "Cancel",
							dialogProps: {
								maxWidth: 'md'
							}
						}
					) == false) {
						return;
					}
				}

				await doSave();
			})
			.catch((error) => {
				console.log('MaterialService.fuzzySearchForDuplicateMaterialNames: ERROR', error);
				setErrorMessage(ErrorProvider.getError(error));
			})
			.finally(() => {
				setIsCheckingNameForDupes(false);
			});
	}

	const clearSaveResult = () => {
		setErrorMessage(undefined);
		setSuccessMessage(undefined);
	};

	return (
		<LocalizationProvider dateAdapter={AdapterDayjs}>
			<section className="hwi-material-details-form">
				{successMessage &&
					<Alert className="alert-success"
						severity="success"
						action={<IconButton onClick={() => { setSuccessMessage(undefined); }} size='small'>X</IconButton>}>
						{successMessage}
					</Alert>
				}
				{errorMessage &&
					<Alert className="alert-error"
						severity="error"
						action={<IconButton onClick={() => { setErrorMessage(undefined); }} size='small'>X</IconButton>}>
						{errorMessage}
					</Alert>
				}
				<Form onSubmit={(event) => handleSubmit(event)}>
					<div className="row">
						<InputLabel>Material *</InputLabel>
						<TextField className="material-field" required autoFocus={action == "add"} autoComplete="off" name="material" value={formFields.material} size="small" onChange={(event) => { setFormFields({ ...formFields, ["material"]: event.target.value }) }} inputProps={{ maxLength: 50 }}></TextField>
					</div>
					<div className="row">
						<InputLabel>Category *</InputLabel>
						<TextField className="category-field" required autoComplete="off" name="category" value={formFields.category} size="small" onChange={(event) => { setFormFields({ ...formFields, ["category"]: event.target.value }) }} inputProps={{ maxLength: 50 }}></TextField>
					</div>
					<div className="row">
						<FormControlLabel control={<Checkbox name="isHazardous" disabled={formFields?.category?.toLowerCase()?.trim() == "hazardous"} checked={formFields.isHazardous} onChange={(event) => { setFormFields({ ...formFields, ["isHazardous"]: event.target.checked }) }}></Checkbox>} label="This material is hazardous." />
					</div>
					<div className="row">
						<FormControlLabel control={<Checkbox name="isActive" checked={formFields.isActive} onChange={(event) => { setFormFields({ ...formFields, ["isActive"]: event.target.checked }) }}></Checkbox>} label="Active" />
					</div>
					<div className="row controls">
						<Button type="submit" className="save-button" disabled={isCheckingNameForDupes || isSaving} variant="contained">{isCheckingNameForDupes || isSaving ? "Saving..." : "Save Details"}</Button>
					</div>
				</Form>
				{/* <Json data={formFields}></Json> */}
			</section>
			<WorkingModal params={workingModalParams?.params}></WorkingModal>
		</LocalizationProvider>
	)
}

export default MaterialDetailsForm;